import React, { useState, ChangeEvent, useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { StudyFields, SecondaryStudyData } from "@/types/types";
import { isNumericField } from "./utils";
import { TruncateWithTooltip } from "./tooltip";
import { sanitizeTextForJson } from "./sanitizeTextForJson";

type HandleSelect = (
  value: string,
  studyFields: StudyFields | null,
  details: SecondaryStudyData
) => void;

interface StudyFieldsComponentProps {
  studyFields: StudyFields | null;
  handleSelect: HandleSelect;
  renderTextAreas?: boolean; // Add this prop
}

function validateInput(
  value: string,
  fieldId: string,
  isComplete: boolean = false
): boolean {
  const trimmed = value.trim().toLowerCase();

  if (!isComplete && ("n/a".startsWith(trimmed) || "na".startsWith(trimmed))) {
    return true;
  }
  if (trimmed === "n/a" || trimmed === "na") {
    return true;
  }

  if (isNumericField(fieldId)) {
    return /^-?\d+(\.\d{0,3})?$/.test(trimmed);
  }
  return true;
}

function isTemperatureField(label: string): boolean {
  return label.toLowerCase().includes("temperature");
}

const StudyFieldsComponent: React.FC<StudyFieldsComponentProps> = ({
  studyFields,
  handleSelect,
  renderTextAreas = false,
}) => {
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});
  const [temperatureUnits, setTemperatureUnits] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    setFieldValues({});
    setFieldErrors({});
    if (studyFields?.fields) {
      const tempUnits: Record<string, string> = {};
      studyFields.fields.forEach((field) => {
        if (isTemperatureField(field.label)) {
          tempUnits[field.id] = "°F";
        }
      });
      setTemperatureUnits(tempUnits);
    }
  }, [studyFields]);

  const handleTemperatureUnitChange = (fieldId: string, unit: string) => {
    setTemperatureUnits((prev) => ({
      ...prev,
      [fieldId]: unit,
    }));

    const currentValue = fieldValues[fieldId];
    if (currentValue && currentValue.trim() !== "") {
      const field = studyFields?.fields?.find((f) => f.id === fieldId);
      if (field) {
        const normalized = currentValue.toLowerCase().trim();
        if (normalized !== "n/a" && normalized !== "na") {
          const numericValue = currentValue.replace(/°[CF]/g, "").trim();
          const newValueWithUnit = numericValue + unit;
          handleSelect(newValueWithUnit, studyFields, field);
        }
      }
    }
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    details: SecondaryStudyData
  ) => {
    let value = e.target.value;

    // Textarea fields (e.g. comments) can contain newlines and pasted
    // rich-text artifacts. Sanitize them before we validate or save.
    if (details.component === "textarea") {
      value = sanitizeTextForJson(value);
    }

    if (validateInput(value, details.label, false)) {
      setFieldValues((prev) => ({
        ...prev,
        [details.id]: value,
      }));

      const isValid = validateInput(value, details.label, true);
      const isEmpty = value.trim() === "";

      setFieldErrors((prev) => ({
        ...prev,
        [details.id]: !isValid && !isEmpty,
      }));

      if (isValid && !isEmpty) {
        let valueToSave = value;
        const normalized = valueToSave.toLowerCase().trim();

        if (normalized === "n/a" || normalized === "na") {
          valueToSave = "N/A";
        } else if (valueToSave !== "") {
          const isNumeric = isNumericField(details.label);

          if (isNumeric) {
            if (normalized.endsWith(".") && /^-?\d+\.$/.test(normalized)) {
              valueToSave = normalized.slice(0, -1);
            }
            if (isTemperatureField(details.label)) {
              const selectedUnit = temperatureUnits[details.id] || "°F";
              valueToSave = valueToSave.trim() + selectedUnit;
            } else if (details.label.toLowerCase().includes("humidity")) {
              valueToSave = valueToSave.trim() + "%";
            }
          }
        }

        handleSelect(valueToSave, studyFields, details);
      } else if (isEmpty) {
        handleSelect("", studyFields, details);
      }
    }
  };

  const renderInputField = (details: SecondaryStudyData) => {
    const value = fieldValues[details.id] ?? "";
    const hasError = fieldErrors[details.id];
    const isNumeric = isNumericField(details.label);
    const isTemp = isTemperatureField(details.label);
    const selectedTempUnit = temperatureUnits[details.id] || "°C";

    return (
      <div key={`${studyFields?.id}-${details?.id}`}>
        <TruncateWithTooltip
          text={details.label || "..."}
          className="block font-medium"
        />
        <div className="relative">
          {details.component === "textarea" ? (
            <textarea
              placeholder={`${details.placeholder}`}
              value={value}
              className={`w-full border rounded px-3 py-2 resize-vertical min-h-[60px] bg-background text-foreground placeholder:text-muted-foreground ${
                hasError ? "border-red-500" : "border-input"
              } focus:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
              onKeyDown={(e) => {
                if (e.key !== "Tab") return;
                // Insert a literal tab character into the textarea instead
                // of letting the browser change focus.
                e.preventDefault();

                const textarea = e.currentTarget;
                const start = textarea.selectionStart ?? value.length;
                const end = textarea.selectionEnd ?? value.length;

                const nextRaw =
                  value.slice(0, start) + "\t" + value.slice(end);
                const sanitized = sanitizeTextForJson(nextRaw);

                setFieldValues((prev) => ({
                  ...prev,
                  [details.id]: sanitized,
                }));

                const isValid = validateInput(
                  sanitized,
                  details.label,
                  true
                );
                const isEmpty = sanitized.trim() === "";

                setFieldErrors((prev) => ({
                  ...prev,
                  [details.id]: !isValid && !isEmpty,
                }));

                // Keep upstream metadata in sync immediately.
                handleSelect(sanitized, studyFields, details);

                // Restore caret position after React re-renders.
                requestAnimationFrame(() => {
                  const caret = start + 1;
                  textarea.setSelectionRange(caret, caret);
                });
              }}
              onChange={(e) => handleInputChange(e, details)}
            />
          ) : (
            <input
              type="text"
              placeholder={`${details.placeholder}`}
              value={value}
              title={details.placeholder}
              className={`w-full border rounded px-3 py-2 bg-background text-foreground placeholder:text-muted-foreground ${
                isTemp ? "pr-16" : isNumeric ? "pr-10" : "pr-3"
              } ${
                hasError ? "border-red-500" : "border-input"
              } focus:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
              onChange={(e) => handleInputChange(e, details)}
            />
          )}

          {isTemp &&
            value.trim().toLowerCase() !== "n/a" &&
            value.trim() !== "" &&
            validateInput(value, details.label, true) && (
              <div className="absolute right-1 top-1/2 -translate-y-1/2">
                <Select
                  value={selectedTempUnit}
                  onValueChange={(unit: string) =>
                    handleTemperatureUnitChange(details.id, unit)
                  }
                >
                  <SelectTrigger className="w-12 h-6 text-xs border border-input bg-background text-foreground px-2 hover:bg-accent focus:ring-0">
                    <SelectValue className="text-xs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="°F" className="text-xs">
                      °F
                    </SelectItem>
                    <SelectItem value="°C" className="text-xs">
                      °C
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

          {isNumeric &&
            !isTemp &&
            value.trim().toLowerCase() !== "n/a" &&
            value.trim() !== "" &&
            validateInput(value, details.label, true) && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                %
              </span>
            )}
        </div>

        {hasError && (
          <span className="text-red-500 text-sm">
            {isNumeric
              ? 'Please enter "N/A" or "na" or a whole number (max 3 decimals).'
              : 'Please enter a valid value or "N/A".'}
          </span>
        )}
      </div>
    );
  };

  const renderTextareaFields = () => {
    return studyFields?.fields
      ?.filter((field) => field.component === "textarea")
      .map((details: SecondaryStudyData) => renderInputField(details));
  };

  if (renderTextAreas) {
    return <div className="w-full">{renderTextareaFields()}</div>;
  }

  return (
    <>
      {studyFields?.fields
        ?.filter((field) => field.component !== "textarea")
        .map((details: SecondaryStudyData) => {
          if (details?.component === "dropdown") {
            return (
              <Select
                onValueChange={(item: string) =>
                  handleSelect(item, studyFields, details)
                }
                key={`${studyFields?.id}-${details?.id}`}
              >
                <div key={`${studyFields?.id}-${details?.id}`}>
                  <label className="block mb-1 font-medium">
                    {details.label}
                  </label>
                  <div className="relative">
                    <SelectTrigger className="w-full">
                      <SelectValue
                        placeholder={details?.label}
                        className="block truncate"
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {details?.values?.map((item: string) => (
                        <SelectItem
                          value={item}
                          key={item}
                          className="block truncate"
                          title="value"
                        >
                          {item}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </div>
                </div>
              </Select>
            );
          }

          if (details?.component === "input") {
            return renderInputField(details);
          }
          return null;
        })}
    </>
  );
};

export default StudyFieldsComponent;
